/**
 * Kiosk time clock — /kiosk (#427 W1b, spec §5 W1b + D4/D5/D11).
 *
 * PUBLIC top-level route (mounted outside ProtectedRoute/AppShell in
 * App.tsx — precedent /intake/:token). There is NO staff session here:
 * the tablet authenticates with a registered device token from
 * localStorage ('ferro.kiosk.deviceToken'), and every punch is a
 * one-shot PIN-verified POST — no JWT is ever minted from a PIN
 * (spec D4). All requests use plain fetch with the device bearer
 * token, never the api.ts session wrapper.
 *
 * Design Philosophy notes:
 *  - P2 (next action obvious / no dead ends): the unregistered state
 *    TEACHES the setup path (Punch Records → Time clock devices →
 *    Register this device); every failure state names the situation + the recovery;
 *    exactly one filled-primary action per screen.
 *  - P3/P6 (brand is the tenant's): the confirmation splash and roster
 *    header wear --brand-* tokens for identity only; status meaning
 *    stays on the structural --ferro-* tokens (success/info).
 *  - P7 (built for hands): tile + key targets are ≥ --ferro-tap; the
 *    PIN pad is an orientation-agnostic grid verified at 768×1024
 *    portrait AND 1024×768 landscape (and fine at 1440/375).
 *  - P5 (never lie): tile tints come from the server roster's punch
 *    status, refreshed after every punch and on a slow poll.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientConfig } from '@ferro/config';

export const KIOSK_DEVICE_TOKEN_KEY = 'ferro.kiosk.deviceToken';

/** Idle on any sub-screen (PIN pad / splash) returns to the roster. */
const IDLE_RESET_MS = 30_000;
/** Confirmation splash auto-clears back to the roster. */
const SPLASH_MS = 5_000;
/** Sawyer 14 (E1): a splash carrying the early clock-in notice stays 12s. */
const SPLASH_NOTICE_MS = 12_000;
/** Roster tint honesty — slow background refresh while idle on roster. */
const ROSTER_POLL_MS = 60_000;
const PIN_LENGTH = 6;

type RosterStatus = 'clocked_out' | 'clocked_in' | 'on_break';

interface RosterEntry {
  id: string;
  first_name: string;
  last_initial: string;
  has_pin: boolean;
  status: RosterStatus;
}

type KioskAction = 'clock_in' | 'clock_out' | 'break_start' | 'break_end';

/** Wire error body — the server's `message` is surfaced VERBATIM. */
interface KioskErrorBody {
  error?: string;
  message?: string;
  attempts_remaining?: number;
  locked_until?: string;
}

type Screen =
  | { kind: 'unregistered' }
  | { kind: 'loading' }
  /** Reachable-but-refused (IP allowlist) — situation + recovery copy. */
  | { kind: 'blocked'; message: string }
  /** Network / unexpected failure — retryable. */
  | { kind: 'error'; message: string }
  | { kind: 'roster' }
  | { kind: 'pin'; staff: RosterEntry }
  | {
      kind: 'splash';
      name: string;
      action: KioskAction;
      at: Date;
      /** Sawyer 14 (E1): the early clock-in sentence, when the server sent one. */
      notice: string | null;
    };

const ACTION_PAST: Record<KioskAction, string> = {
  clock_in: 'clocked in',
  clock_out: 'clocked out',
  break_start: 'on break',
  break_end: 'back from break',
};

function fmtTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function readDeviceToken(): string | null {
  try {
    return window.localStorage.getItem(KIOSK_DEVICE_TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearDeviceToken(): void {
  try {
    window.localStorage.removeItem(KIOSK_DEVICE_TOKEN_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/**
 * Plain-fetch helper for the kiosk API (NOT the api.ts wrapper — there
 * is no staff session on this surface). Returns status + parsed body;
 * throws only on network failure.
 */
async function kioskFetch(
  apiUrl: string,
  path: string,
  token: string,
  init?: { method?: string; body?: unknown },
): Promise<{ status: number; body: Record<string, unknown> }> {
  const res = await fetch(`${apiUrl}${path}`, {
    method: init?.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.body !== undefined
        ? { 'Content-Type': 'application/json' }
        : {}),
    },
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    // Non-JSON body (proxy error page) — status carries the signal.
  }
  return { status: res.status, body };
}

const NETWORK_ERROR_COPY =
  'The time clock can’t reach the server — check the internet connection and try again. Punches were NOT recorded.';

export function Kiosk({ config }: { config: ClientConfig }): JSX.Element {
  // Same apiUrl derivation as Login.tsx — the deployment config owns it.
  const apiUrl = config.deployment.apiUrl;

  const [screen, setScreen] = useState<Screen>({ kind: 'loading' });
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [punchPending, setPunchPending] = useState(false);
  const [clock, setClock] = useState(() => new Date());
  // Bumped on every keypad/user interaction so the idle timer restarts.
  const [activityTick, setActivityTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Live clock — a time clock should show the time (tabular-nums in CSS).
  useEffect(() => {
    const id = window.setInterval(() => setClock(new Date()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const resetToRoster = useCallback(() => {
    setPin('');
    setPinError(null);
    setScreen({ kind: 'roster' });
  }, []);

  /** Fetch /status then /roster; drives the top-level screen. */
  const loadKiosk = useCallback(async (): Promise<void> => {
    const token = readDeviceToken();
    if (!token) {
      setScreen({ kind: 'unregistered' });
      return;
    }
    setScreen({ kind: 'loading' });
    try {
      const status = await kioskFetch(apiUrl, '/api/kiosk/v1/status', token);
      if (!mountedRef.current) return;
      if (status.status === 401) {
        // device_not_registered (or revoked) — the stored token is dead.
        clearDeviceToken();
        setScreen({ kind: 'unregistered' });
        return;
      }
      if (status.status !== 200) {
        setScreen({ kind: 'error', message: NETWORK_ERROR_COPY });
        return;
      }
      if (status.body['ip_allowed'] === false) {
        setScreen({
          kind: 'blocked',
          message:
            typeof status.body['message'] === 'string'
              ? status.body['message']
              : 'This network isn’t approved for the time clock — a manager can update the allowed networks in Punch Records → Time clock devices.',
        });
        return;
      }
      const rosterRes = await kioskFetch(apiUrl, '/api/kiosk/v1/roster', token);
      if (!mountedRef.current) return;
      if (rosterRes.status === 401) {
        clearDeviceToken();
        setScreen({ kind: 'unregistered' });
        return;
      }
      if (rosterRes.status === 403) {
        setScreen({
          kind: 'blocked',
          message:
            typeof rosterRes.body['message'] === 'string'
              ? rosterRes.body['message']
              : 'This network isn’t approved for the time clock — a manager can update the allowed networks in Punch Records → Time clock devices.',
        });
        return;
      }
      if (rosterRes.status !== 200) {
        setScreen({ kind: 'error', message: NETWORK_ERROR_COPY });
        return;
      }
      const staff = Array.isArray(rosterRes.body['staff'])
        ? (rosterRes.body['staff'] as RosterEntry[])
        : [];
      setRoster(staff);
      setScreen({ kind: 'roster' });
    } catch {
      if (!mountedRef.current) return;
      setScreen({ kind: 'error', message: NETWORK_ERROR_COPY });
    }
  }, [apiUrl]);

  useEffect(() => {
    void loadKiosk();
  }, [loadKiosk]);

  /** Silent roster refresh (tile tints stay honest) — never disruptive. */
  const refreshRoster = useCallback(async (): Promise<void> => {
    const token = readDeviceToken();
    if (!token) return;
    try {
      const res = await kioskFetch(apiUrl, '/api/kiosk/v1/roster', token);
      if (!mountedRef.current || res.status !== 200) return;
      if (Array.isArray(res.body['staff'])) {
        setRoster(res.body['staff'] as RosterEntry[]);
      }
    } catch {
      // Background refresh only — the visible roster stays as-is.
    }
  }, [apiUrl]);

  useEffect(() => {
    if (screen.kind !== 'roster') return undefined;
    const id = window.setInterval(() => void refreshRoster(), ROSTER_POLL_MS);
    return () => window.clearInterval(id);
  }, [screen.kind, refreshRoster]);

  // Idle auto-reset: 30s of no interaction on the PIN screen → roster.
  useEffect(() => {
    if (screen.kind !== 'pin') return undefined;
    const id = window.setTimeout(resetToRoster, IDLE_RESET_MS);
    return () => window.clearTimeout(id);
  }, [screen, activityTick, resetToRoster]);

  // Confirmation splash auto-clears after 5s.
  useEffect(() => {
    if (screen.kind !== 'splash') return undefined;
    const id = window.setTimeout(() => {
      resetToRoster();
      void refreshRoster();
    }, screen.notice ? SPLASH_NOTICE_MS : SPLASH_MS);
    return () => window.clearTimeout(id);
  }, [screen, resetToRoster, refreshRoster]);

  const bumpActivity = (): void => setActivityTick((t) => t + 1);

  const openPinFor = (staff: RosterEntry): void => {
    setPin('');
    setPinError(null);
    setScreen({ kind: 'pin', staff });
  };

  const pressDigit = (d: string): void => {
    bumpActivity();
    setPinError(null);
    setPin((p) => (p.length >= PIN_LENGTH ? p : p + d));
  };

  const pressBackspace = (): void => {
    bumpActivity();
    setPinError(null);
    setPin((p) => p.slice(0, -1));
  };

  const punch = async (
    staff: RosterEntry,
    action: KioskAction,
    breakKind?: 'rest' | 'meal',
  ): Promise<void> => {
    bumpActivity();
    const token = readDeviceToken();
    if (!token) {
      setScreen({ kind: 'unregistered' });
      return;
    }
    setPunchPending(true);
    setPinError(null);
    try {
      const res = await kioskFetch(apiUrl, '/api/kiosk/v1/punch', token, {
        method: 'POST',
        body: {
          staffUserId: staff.id,
          pin,
          action,
          ...(breakKind ? { breakKind } : {}),
        },
      });
      if (!mountedRef.current) return;
      if (res.status === 200) {
        const body = res.body as {
          staff?: { first_name?: string };
          action?: KioskAction;
          at?: string;
          early_notice?: string | null;
        };
        setScreen({
          kind: 'splash',
          name: body.staff?.first_name ?? staff.first_name,
          action: body.action ?? action,
          at: body.at ? new Date(body.at) : new Date(),
          // Absent on a server without Sawyer 14 → no second line (AC21).
          notice:
            typeof body.early_notice === 'string' && body.early_notice.length > 0
              ? body.early_notice
              : null,
        });
        setPin('');
        void refreshRoster();
        return;
      }
      const err = res.body as KioskErrorBody;
      if (res.status === 401 && err.error === 'device_not_registered') {
        // Revoked mid-session — flip to the teaching state, drop the token.
        clearDeviceToken();
        setScreen({ kind: 'unregistered' });
        return;
      }
      if (res.status === 403 && err.error === 'ip_not_allowed') {
        setScreen({
          kind: 'blocked',
          message:
            err.message ??
            'This network isn’t approved for the time clock — a manager can update the allowed networks in Punch Records → Time clock devices.',
        });
        return;
      }
      // pin_invalid (401) / staff_locked / device_locked (423) /
      // pin_not_set / already_clocked_in / … (409): the server's message
      // field is situation+recovery copy — surface it verbatim.
      setPin('');
      setPinError(
        err.message ?? 'That didn’t go through — please try again.',
      );
    } catch {
      if (!mountedRef.current) return;
      setPinError(NETWORK_ERROR_COPY);
    } finally {
      if (mountedRef.current) setPunchPending(false);
    }
  };

  // ── Screens ──────────────────────────────────────────────────────────

  if (screen.kind === 'unregistered') {
    return (
      <main className="kiosk-page" data-testid="kiosk-page">
        <div className="kiosk-teach" data-testid="kiosk-unregistered">
          <h1 className="kiosk-teach__title">Time clock</h1>
          <p className="kiosk-teach__lead">
            This device isn&rsquo;t set up as the time clock yet.
          </p>
          {/* P2 — no dead ends: teach the exact setup path. */}
          <ol className="kiosk-teach__steps">
            <li>A manager signs in to the admin app on this device.</li>
            <li>
              Open <strong>Punch Records &rarr; Time clock devices</strong> and tap{' '}
              <strong>&ldquo;Register this device&rdquo;</strong>.
            </li>
            <li>Come back to this page &mdash; the staff roster appears.</li>
          </ol>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary kiosk-teach__retry"
            onClick={() => void loadKiosk()}
          >
            Check again
          </button>
        </div>
      </main>
    );
  }

  if (screen.kind === 'loading') {
    return (
      <main className="kiosk-page" data-testid="kiosk-page">
        <div
          className="kiosk-roster-skeleton"
          data-testid="kiosk-skeleton"
          aria-hidden="true"
        >
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="kiosk-tile-skeleton" />
          ))}
        </div>
      </main>
    );
  }

  if (screen.kind === 'blocked' || screen.kind === 'error') {
    return (
      <main className="kiosk-page" data-testid="kiosk-page">
        <div className="kiosk-teach" role="alert" data-testid="kiosk-blocked">
          <h1 className="kiosk-teach__title">Time clock</h1>
          <p className="kiosk-teach__lead">{screen.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary kiosk-teach__retry"
            onClick={() => void loadKiosk()}
          >
            Try again
          </button>
        </div>
      </main>
    );
  }

  if (screen.kind === 'splash') {
    return (
      <main className="kiosk-page kiosk-page--splash" data-testid="kiosk-page">
        {/* Brand-colored identity moment (P3) — auto-clears in 5s. */}
        <div className="kiosk-splash" data-testid="kiosk-splash" role="status">
          <span className="kiosk-splash__check" aria-hidden="true">
            ✓
          </span>
          <p className="kiosk-splash__line">
            <strong>{screen.name}</strong> &mdash; {ACTION_PAST[screen.action]}{' '}
            <span className="kiosk-splash__time">{fmtTime(screen.at)}</span>
          </p>
          {screen.notice ? (
            <p className="kiosk-splash__notice" data-testid="kiosk-splash-notice">
              {screen.notice}
            </p>
          ) : null}
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost kiosk-splash__done"
            onClick={() => {
              resetToRoster();
              void refreshRoster();
            }}
          >
            Done
          </button>
        </div>
      </main>
    );
  }

  if (screen.kind === 'pin') {
    const staff = screen.staff;
    const pinFull = pin.length === PIN_LENGTH;
    return (
      <main className="kiosk-page" data-testid="kiosk-page">
        <div className="kiosk-pin" data-testid="kiosk-pin">
          <header className="kiosk-pin__head">
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost kiosk-pin__back"
              onClick={resetToRoster}
            >
              &larr; Back
            </button>
            <h1 className="kiosk-pin__name">
              {staff.first_name} {staff.last_initial}.
            </h1>
          </header>

          <div
            className="kiosk-pin__dots"
            aria-label={`PIN — ${pin.length} of ${PIN_LENGTH} digits entered`}
          >
            {Array.from({ length: PIN_LENGTH }, (_, i) => (
              <span
                key={i}
                className={`kiosk-pin__dot${i < pin.length ? ' kiosk-pin__dot--filled' : ''}`}
                aria-hidden="true"
              />
            ))}
          </div>

          {pinError ? (
            <p className="kiosk-pin__error" role="alert" data-testid="kiosk-pin-error">
              {pinError}
            </p>
          ) : !staff.has_pin ? (
            <p className="kiosk-pin__hint">
              No PIN on file yet &mdash; set one from your own login, or ask a
              manager.
            </p>
          ) : null}

          {!pinFull ? (
            /* Orientation-agnostic 3-column pad, giant ≥tap-size keys. */
            <div className="kiosk-pad" role="group" aria-label="PIN pad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button
                  key={d}
                  type="button"
                  className="kiosk-key"
                  onClick={() => pressDigit(d)}
                >
                  {d}
                </button>
              ))}
              <span className="kiosk-key kiosk-key--blank" aria-hidden="true" />
              <button
                type="button"
                className="kiosk-key"
                onClick={() => pressDigit('0')}
              >
                0
              </button>
              <button
                type="button"
                className="kiosk-key kiosk-key--backspace"
                onClick={pressBackspace}
                aria-label="Delete last digit"
              >
                &#9003;
              </button>
            </div>
          ) : (
            /* PIN complete → the status-appropriate actions (one filled
               primary per view — P2). The PIN rides along one-shot. */
            <div className="kiosk-actions" data-testid="kiosk-actions">
              {staff.status === 'clocked_out' ? (
                <button
                  type="button"
                  className="ferro-btn ferro-btn--primary kiosk-action kiosk-action--hero"
                  disabled={punchPending}
                  onClick={() => void punch(staff, 'clock_in')}
                >
                  {punchPending ? 'Punching…' : 'Clock in'}
                </button>
              ) : staff.status === 'on_break' ? (
                <button
                  type="button"
                  className="ferro-btn ferro-btn--primary kiosk-action kiosk-action--hero"
                  disabled={punchPending}
                  onClick={() => void punch(staff, 'break_end')}
                >
                  {punchPending ? 'Punching…' : 'End break'}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--primary kiosk-action kiosk-action--hero"
                    disabled={punchPending}
                    onClick={() => void punch(staff, 'clock_out')}
                  >
                    {punchPending ? 'Punching…' : 'Clock out'}
                  </button>
                  <div className="kiosk-actions__row">
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--outline kiosk-action"
                      disabled={punchPending}
                      onClick={() => void punch(staff, 'break_start', 'rest')}
                    >
                      Start rest break
                    </button>
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--outline kiosk-action"
                      disabled={punchPending}
                      onClick={() => void punch(staff, 'break_start', 'meal')}
                    >
                      Start meal break
                    </button>
                  </div>
                </>
              )}
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost kiosk-action"
                disabled={punchPending}
                onClick={() => {
                  bumpActivity();
                  setPin('');
                  setPinError(null);
                }}
              >
                Re-enter PIN
              </button>
            </div>
          )}
        </div>
      </main>
    );
  }

  // ── Roster ────────────────────────────────────────────────────────────
  return (
    <main className="kiosk-page" data-testid="kiosk-page">
      <header className="kiosk-header">
        <span className="kiosk-header__brand">{config.brand.name}</span>
        <h1 className="kiosk-header__title">Time clock</h1>
        <span className="kiosk-header__clock" data-testid="kiosk-clock">
          {fmtTime(clock)}
        </span>
      </header>

      {roster.length === 0 ? (
        <div className="kiosk-teach" data-testid="kiosk-roster-empty">
          <p className="kiosk-teach__lead">
            No active staff to show yet &mdash; add your team in the admin
            app under <strong>Staff</strong>, then their names appear here.
          </p>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary kiosk-teach__retry"
            onClick={() => void loadKiosk()}
          >
            Refresh
          </button>
        </div>
      ) : (
        <div className="kiosk-roster" data-testid="kiosk-roster">
          {roster.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`kiosk-tile kiosk-tile--${s.status}`}
              onClick={() => openPinFor(s)}
              data-testid={`kiosk-tile-${s.id}`}
            >
              <span className="kiosk-tile__name">
                {s.first_name} {s.last_initial}.
              </span>
              <span className="kiosk-tile__status">
                {s.status === 'clocked_in'
                  ? 'Clocked in'
                  : s.status === 'on_break'
                    ? 'On break'
                    : s.has_pin
                      ? 'Clocked out'
                      : 'No PIN yet'}
              </span>
            </button>
          ))}
        </div>
      )}
    </main>
  );
}

export default Kiosk;
